import { useGetEmployeeMe } from "@/shared/api";
import { resetAuth } from "@/shared/lib";
import { vars } from "@/shared/lib/theme.css";
import { Column, Row, SidebarButton, TextButton, Typography } from "@/shared/ui";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { HTMLAttributes, memo, useEffect } from "react";
import { IconType } from "react-icons/lib";
import { SidebarVariants, sidebar } from "./index.css";

// 조회 메뉴만 접근 가능한 제한된 사용자 목록
const RESTRICTED_USERS = ["류영아", "김영미"];
// 제한된 사용자에게 허용되는 경로
const ALLOWED_PATHS_FOR_RESTRICTED = ["/search", "/prize"];

type SidebarPath = {
	icon: IconType;
	title: string;
	to: string;
	notClick?: boolean;
	authority?: string[];
	department?: string[];
	children?: SidebarPath[];
};

type SidebarProps = { paths: SidebarPath[] } & HTMLAttributes<HTMLSpanElement> & SidebarVariants;

function Sidebar({ paths }: SidebarProps) {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { data } = useGetEmployeeMe();

	useEffect(() => {
		if (data) Sentry.setUser({ id: data.id, username: data.name, email: data.department });
	}, [data]);

	return (
		<aside className={sidebar()} style={{ height: "100vh" }}>
			<Row fullWidth style={{ padding: vars.size[16], gap: vars.size[8], justifyContent: "space-between" }}>
				<Column style={{ gap: vars.size[8] }}>
					<Row style={{ gap: vars.size[4] }}>
						<Typography size={vars.size[14]} fontWeight="bold">
							{data?.name}
						</Typography>
						<Typography size={vars.size[12]}>{data?.position}</Typography>
					</Row>
					<Row style={{ gap: vars.size[4] }}>
						<Typography size={vars.size[12]}>{data?.department}</Typography>
					</Row>
				</Column>
				<Column style={{ gap: vars.size[8] }}>
					<TextButton
						text="로그아웃"
						buttonProps={{
							size: "small",
							color: "secondary",
							onClick: () => {
								queryClient.clear();
								resetAuth();
								navigate({
									to: "/login",
									search: {
										step: "OPENING"
									}
								});
							}
						}}
					/>
				</Column>
			</Row>

			<SidebarTree paths={paths} />
		</aside>
	);
}

function SidebarTree({ paths }: SidebarProps) {
	const { data } = useGetEmployeeMe();
	const authority = data?.authority ?? "";
	const department = data?.department ?? "";
	const userName = data?.name ?? "";

	// 제한된 사용자인지 확인
	const isRestrictedUser = RESTRICTED_USERS.includes(userName);

	// 경로가 표시 가능한지 확인하는 함수
	const isPathVisible = (path: SidebarPath): boolean => {
		// 제한된 사용자인 경우 허용된 경로만 표시
		if (isRestrictedUser) {
			return ALLOWED_PATHS_FOR_RESTRICTED.some((allowedPath) => path.to.startsWith(allowedPath));
		}

		// 일반 사용자: 기존 authority/department 로직
		return (
			(!path.authority && !path.department) ||
			!!(path.authority?.includes(authority) && path.department?.includes(department))
		);
	};

	return (
		<Column
			fullWidth
			style={{
				padding: vars.size[8],
				alignItems: "flex-start",
				gap: vars.size[14]
			}}
		>
			{paths.map((path) =>
				isPathVisible(path) ? (
					<Column key={path.to} fullWidth style={{ alignItems: "flex-start", gap: vars.size[4] }}>
						<SidebarTreeItem key={path.to} path={path} />

						{path.children && (
							<Column
								fullWidth
								style={{
									alignItems: "flex-start",
									gap: vars.size[4],
									paddingLeft: vars.size[16]
								}}
							>
								{path.children.map((cPath) => (
									<SidebarTreeItem key={cPath.to} path={cPath} />
								))}
							</Column>
						)}
					</Column>
				) : (
					<></>
				)
			)}
		</Column>
	);
}

const SidebarTreeItem = memo(function SidebarTreeItem({ path }: { path: SidebarPath }) {
	const navigate = useNavigate();
	const location = useLocation();
	const pathName = location.pathname;
	const notClick = path.notClick ?? false;

	return (
		<SidebarButton
			icon={path.icon}
			title={path.title}
			active={path.to === pathName}
			isUnclickable={notClick}
			onClick={() => {
				!notClick && navigate({ to: path.to });
			}}
		/>
	);
});

export default Sidebar;

